import classNames from 'classnames'
import Icon from 'common/Icon'
import { CHARCOAL_BLACK, DIM_GREY, YELLOW } from 'constants/colors'
import { Button, Spinner } from 'react-bootstrap'
import styles from './index.module.scss'

type ModalContainerProps = {
  title?: string
  ariaModal?: boolean
  ariaLabel?: string
  subtext?: string
  subtextDark?: boolean
  showSubtextIcon?: boolean // This is used in support cancel appointment modal
  backText?: string
  submitText?: string
  submitId?: string
  isSubmitDisabled?: boolean
  isSubmitting?: boolean
  disableParentOverflow?: boolean
  submitClassName?: string
  closeClassName?: string
  showBackIcon?: boolean
  onSubmit?: () => void
  onClose?: () => void
  onCloseIcon?: () => void //This is used to show the cross icon on the top
  titleClassName?: string
  showBottomBorder?: boolean
  hasSubmitAndCancelButtons?: boolean
  hasGrayHeaderBackground?: boolean
}

const ModalContainer: React.FC<ModalContainerProps> = ({
  children,
  title,
  ariaModal,
  ariaLabel,
  subtext,
  subtextDark = false,
  showSubtextIcon = false,
  submitId = '',
  backText = 'Back',
  submitText = 'Apply',
  isSubmitDisabled = false,
  isSubmitting = false,
  disableParentOverflow = false,
  showBackIcon = true,
  submitClassName = '',
  closeClassName = '',
  onSubmit,
  onClose,
  onCloseIcon,
  titleClassName = '',
  showBottomBorder = true,
  hasSubmitAndCancelButtons = true,
  hasGrayHeaderBackground = true,
}) => {
  return (
    <div
      className={classNames('position-relative d-flex flex-column h-100', {
        'overflow-auto': !disableParentOverflow,
      })}
      aria-modal={ariaModal}
      role={ariaLabel ? 'dialog' : null}
      aria-label={ariaLabel}
    >
      {onCloseIcon && (
        <Button
          variant="tertiary"
          className={styles.close_btn}
          onClick={onCloseIcon}
        >
          <Icon icon="lightCross" className="fa-md" fill={DIM_GREY} />
        </Button>
      )}
      {(title || subtext) && (
        <div
          className={
            hasGrayHeaderBackground
              ? styles.modal_header_container
              : styles.modal_header_container_white
          }
        >
          <div className={`${styles.modal_header_title} ${titleClassName}`}>
            {title}
          </div>
          {subtext && (
            <div
              className={classNames('fs-14', {
                'secondary-text': !subtextDark,
                [styles.modal_subtext]: subtextDark,
              })}
            >
              {showSubtextIcon && (
                <>
                  <Icon icon="warning" className="fa-lg" fill={YELLOW} />{' '}
                </>
              )}
              {subtext}
            </div>
          )}
        </div>
      )}
      <div
        className={classNames('flex-fill  mr-2', {
          'overflow-auto': !disableParentOverflow,
        })}
      >
        {children}
      </div>
      {(showBottomBorder || hasSubmitAndCancelButtons) && (
        <div className="d-flex flex-column-reverse flex-sm-row justify-content-end px-sm-20 px-md-3 py-3 position-relative">
          {showBottomBorder && (
            <div className="position-absolute top-0 left-0 right-0 bg-light height-1" />
          )}
          {onClose && hasSubmitAndCancelButtons && (
            <Button
              variant="none"
              onClick={onClose}
              className={`${closeClassName} ${styles.modal_action_button} flex-center font-weight-medium fs-16`}
            >
              {showBackIcon && (
                <Icon
                  icon="back"
                  fill={CHARCOAL_BLACK}
                  className="fa-md mr-2"
                />
              )}
              {backText}
            </Button>
          )}
          {onSubmit && hasSubmitAndCancelButtons && (
            <Button
              id={submitId}
              variant="info"
              disabled={isSubmitDisabled || isSubmitting}
              onClick={onSubmit}
              className={`${submitClassName} ${styles.modal_action_button} fs-16 font-weight-input mt-1 mt-sm-0 mb-3 mb-sm-0 py-2`}
            >
              {isSubmitting ? <Spinner animation="border" /> : submitText}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default ModalContainer
